export enum FetchPriority {
    LOW = 'low',
    HIGH = 'high',
    AUTO = 'auto',
}

export enum CrossOrigin {
    ANONYMOUS = 'anonymous',
    USE_CREDENTIALS = 'use-credentials',
}

/**
 * Defines which and how to load a script file.
 */
export interface LoadScriptOptions {
    /** an optional element id for the script to find or remove it later */
    id?: string;
    /** the URL to an external javascript file */
    src: string;
    /** Indicate to a browser that the script can be fetched in parallel to parsing and should evaluated as soon as it is available. */
    async?: boolean;
    /** Indicate to a browser that the script is meant to be executed after the document has been parsed, but before firing DOMContentLoaded. */
    defer?: boolean;
    /** an optional document node to append the script to. */
    node?: Node;
    /** an optional fetchpriority attribute for the script */
    fetchpriority?: FetchPriority;
    /** sets Cross-Origin Reource Sharing settings for the script element */
    crossOrigin?: CrossOrigin,
    /** an object to create data-attributes on the script */
    data?: Record<string, string>
    /** an optional callback function, which is invoked when the script successfully loads */
    onload?: () => void,
    /** an optional callback function, which is invoked if the script can not be loaded */
    onerror?: OnErrorEventHandler,
}

/**
 * Load a javascript file into the browser.
 *
 * @param options {LoadScriptOptions} define what and how to load. Defaults: async: true, defer: false
 * @returns the newly created script element
 */
export const loadScript = (options: LoadScriptOptions): HTMLScriptElement => {
    const NOOP = () => { };
    const { id, src, async = true, defer = false, node, fetchpriority, crossOrigin, data = {}, onload = NOOP, onerror = NOOP } = options;
    const doc = node?.ownerDocument || document;
    const script = doc.createElement('script');
    if (!doc.characterSet || !(/UTF-8/i.test(doc.characterSet))) {
        script.charset = 'UTF-8';
    }
    Object.entries(data).forEach(([key, value]) => {
        script.setAttribute(`data-${key}`, value);
    });
    if (fetchpriority) script.setAttribute('fetchpriority', fetchpriority); // https://web.dev/fetch-priority/
    if (crossOrigin) script.crossOrigin = crossOrigin;
    script.type = 'text/javascript';
    script.onerror = onerror;
    script.onload = onload;
    if (id) script.id = id;
    script.async = async;
    script.defer = defer;
    script.src = src;
    if (node) {
        node.appendChild(script);
    }
    return script;
};
